﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Link , useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useLoginMutation,
  useResendVerificationMutation,
} from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

const schema = z.object({
  email: z.string().email("validation.emailInvalid"),
  password: z.string().min(1, "validation.passwordRequired"),
});

type LoginFormValues = z.infer<typeof schema>;

export function LoginPage() {
  const t = useT("auth");
  const tCommon = useT("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useLoginMutation();
  const resend = useResendVerificationMutation();

  const from = searchParams.get("from") ?? "/";

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  async function handleResend(email: string) {
    try {
      await resend.mutateAsync({ email });
      toast.success(t("loginPage.resendSuccess"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : t("loginPage.resendFailed"));
    }
  }

  async function onSubmit(values: LoginFormValues) {
    try {
      await login.mutateAsync(values);
      toast.success(t("loginPage.success"));
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          toast.error(t("tooManyAttempts"));
          return;
        }
        if (err.code === "email_not_verified") {
          toast.error(err.detail, {
            duration: 10000,
            action: {
              label: t("loginPage.resend"),
              onClick: () => handleResend(values.email),
            },
          });
          return;
        }
        toast.error(err.detail);
      } else {
        toast.error(tCommon("unexpectedError"));
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{t("loginPage.title")}</CardTitle>
          <CardDescription>{t("loginPage.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("email")}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder={t("emailPlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t("password")}</FormLabel>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {t("forgotPassword")}
                      </Link>
                    </div>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending ? t("loginPage.submitting") : t("loginPage.submit")}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("loginPage.noAccount")}{" "}
            <Link to="/signup" className="font-medium text-foreground hover:underline">
              {t("loginPage.createAccount")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
